/**
 * HAT-1364: a saved column layout must still fit the screen it is shown on.
 *
 * Dragging a column edge freezes every column at an absolute pixel width, and that
 * includes the board's flexible column - the one declared without a fixed width, which
 * on a wide screen had quietly absorbed all the slack. The sum of the saved widths is
 * then a constant that knows nothing about the window: at 100% zoom instead of 90%, or
 * with the sidebar open, the table overflows by exactly the trailing columns (status,
 * row actions), and the sideways scrollbar sits below the fold where nobody finds it.
 *
 * This keeps the saved widths as the user's *preference* and, only when they do not
 * fit, takes the excess back from the flexible columns - proportionally, and never
 * below each one's floor. Fixed columns are never touched, and nothing is widened: a
 * layout that fits is returned as it was saved. Only when the fixed columns alone
 * outgrow the screen does the table still scroll sideways.
 */

export interface FitColumn {
  id: string;
  /** May give up width when the layout does not fit. */
  flexible: boolean;
  /** The narrowest a flexible column may become. */
  minWidthPx: number;
}

/** Tailwind utilities that pin a column: `w-[100px]`, `max-w-[100px]`. `min-w-[...]` does not. */
const FIXED_WIDTH_CLASS = /(^|\s)(max-)?w-\[/;
const MIN_WIDTH_CLASS = /(^|\s)min-w-\[(\d+(?:\.\d+)?)px\]/;

/**
 * Whether a board column is flexible, from how the board declared it. A column with no
 * `w-[...]`/`max-w-[...]` utility is sized by the table from what its neighbours leave over,
 * which is exactly the width that must not be frozen.
 */
export const isFlexibleColumnClass = (className: string | undefined): boolean => !FIXED_WIDTH_CLASS.test(className ?? '');

/** The `min-w-[Npx]` floor a board declared on a column, if any. */
export const declaredMinWidthPx = (className: string | undefined): number | undefined => {
  const match = MIN_WIDTH_CLASS.exec(className ?? '');
  return match ? Number(match[2]) : undefined;
};

/**
 * Returns the widths to render. Same object as `widths` when nothing has to change, so
 * callers can rely on identity to skip work.
 */
export const fitColumnWidths = (widths: Record<string, number>, columns: FitColumn[], availableWidthPx: number): Record<string, number> => {
  if (!availableWidthPx || columns.length === 0) return widths;
  // Half a layout cannot be fitted: the table has not measured the newcomers yet.
  if (columns.some((column) => !widths[column.id])) return widths;

  const total = columns.reduce((sum, column) => sum + widths[column.id], 0);
  const excess = total - availableWidthPx;
  if (excess <= 0) return widths;

  const givers = columns.filter((column) => column.flexible && widths[column.id] > column.minWidthPx);
  const giveable = givers.reduce((sum, column) => sum + widths[column.id] - column.minWidthPx, 0);
  if (giveable <= 0) return widths;

  const reduceBy = Math.min(excess, giveable);
  const fitted = { ...widths };
  let taken = 0;
  givers.forEach((column, index) => {
    const room = widths[column.id] - column.minWidthPx;
    // The last giver absorbs the rounding so the columns add up to the exact target.
    const share = index === givers.length - 1 ? reduceBy - taken : Math.round((reduceBy * room) / giveable);
    fitted[column.id] = widths[column.id] - share;
    taken += share;
  });
  return fitted;
};
